import { Icon } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { ActivityStep, TurnActivity } from '@monetize/types/app';
import { FC, memo, useEffect, useId, useState } from 'react';
import { IconType } from 'react-icons';
import {
  MdBlock,
  MdCheck,
  MdChevronRight,
  MdErrorOutline,
  MdExpandMore,
  MdOutlineChatBubbleOutline,
} from 'react-icons/md';
import { getRunningStep } from '../../services/chatActivity';
import { MBox, MFlex, MSpinner, MText } from '../Monetize';
import {
  buildTimeline,
  formatElapsed,
  getLiveActivityLabel,
  getStepLabel,
  RECONNECTING_LABEL,
  TimelineEntry,
} from './activityLabels';
import { MontyAvatar } from './MontyAvatar';
import { useSteadyValue } from './useSteadyValue';

/** Steps often take well under a second; each live label stays up at least
 * this long so it can be read. */
const LIVE_LABEL_HOLD_MS = 1000;

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

/** Each new live label settles in rather than snapping. */
const labelEnter = keyframes`
  from { opacity: 0; transform: translateY(3px); }
  to { opacity: 1; transform: translateY(0); }
`;

/** Moving highlight across "in progress" text. */
const SHIMMER_PROPS = {
  bgImage:
    'linear-gradient(90deg, {colors.fg.muted} 35%, {colors.tMagenta.base} 50%, {colors.fg.muted} 65%)',
  bgSize: '200% 100%',
  bgClip: 'text',
  color: 'transparent',
  animation: `${shimmer} 2.2s linear infinite`,
  _motionReduce: { animation: 'none', bgImage: 'none', color: 'fg.muted' },
} as const;

/** Re-renders itself once a second so the rest of the chat doesn't have to. */
const useNow = (isTicking: boolean) => {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!isTicking) {
      return;
    }
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isTicking]);
  return now;
};

const ElapsedSince: FC<{ startedAt: number }> = ({ startedAt }) => {
  const now = useNow(true);
  const elapsed = now - startedAt;
  return elapsed < 1000 ? 'Working…' : `Working for ${formatElapsed(elapsed)}`;
};

const STATUS_ICONS: Record<
  Exclude<ActivityStep['status'], 'running'>,
  { icon: IconType; color: string }
> = {
  done: { icon: MdCheck, color: 'fg.subtle' },
  failed: { icon: MdErrorOutline, color: 'tRed.base' },
  stopped: { icon: MdBlock, color: 'fg.subtle' },
};

const StepIcon: FC<{ step: ActivityStep }> = ({ step }) => {
  if (step.kind === 'note') {
    return (
      <Icon
        as={MdOutlineChatBubbleOutline}
        boxSize="13px"
        color="fg.subtle"
        flexShrink={0}
        aria-hidden
      />
    );
  }
  if (step.status === 'running') {
    return (
      <MSpinner
        size="xs"
        color="action.ai"
        borderWidth="1.5px"
        boxSize="12px"
        flexShrink={0}
        aria-hidden
      />
    );
  }
  const { icon, color } = STATUS_ICONS[step.status];
  return (
    <Icon as={icon} boxSize="14px" color={color} flexShrink={0} aria-hidden />
  );
};

/** Pretty-prints JSON tool arguments/results; capped text stays as-is. */
const formatPayload = (payload: string) => {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

const Payload: FC<{ label: string; payload: string }> = ({
  label,
  payload,
}) => (
  <MBox>
    <MText fontSize="xxs" fontWeight={600} color="fg.muted" mb={1}>
      {label}
    </MText>
    <MBox
      as="pre"
      fontFamily="mono"
      fontSize="xxs"
      lineHeight="1.5"
      color="fg.primary"
      bg="bg.subtle"
      border="1px solid"
      borderColor="border.subtle"
      borderRadius="md"
      px={2.5}
      py={2}
      maxH="180px"
      overflow="auto"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
    >
      {formatPayload(payload)}
    </MBox>
  </MBox>
);

const hasPayload = (input?: string): input is string =>
  !!input && input !== '{}';

const StepRow: FC<TimelineEntry> = ({ step, reason }) => {
  const [isOpen, setIsOpen] = useState(false);
  const detailsId = useId();
  const { text, detail } = getStepLabel(step);
  const isRunning = step.status === 'running';
  const canOpen =
    step.kind === 'tool' &&
    (!!reason || hasPayload(step.input) || !!step.output);

  const label = (
    <MFlex alignItems="center" gap={2} minW={0} flex={1}>
      <StepIcon step={step} />
      <MText
        fontSize="xs"
        color={step.kind === 'note' ? 'fg.muted' : 'fg.primary'}
        fontStyle={step.kind === 'note' ? 'italic' : undefined}
        truncate={step.kind !== 'note'}
        {...(isRunning ? SHIMMER_PROPS : undefined)}
      >
        {text}
      </MText>
      {detail && (
        <MText
          fontSize="xs"
          color={step.status === 'failed' ? 'tRed.base' : 'fg.subtle'}
          flexShrink={0}
        >
          {detail}
        </MText>
      )}
    </MFlex>
  );

  if (!canOpen) {
    return (
      <MBox py={1} px={1.5}>
        {label}
      </MBox>
    );
  }

  return (
    <MBox>
      <MFlex
        width="100%"
        alignItems="center"
        gap={1}
        py={1}
        px={1.5}
        borderRadius="md"
        textAlign="left"
        cursor="pointer"
        color="fg.subtle"
        _hover={{ bg: 'bg.hover', color: 'fg.muted' }}
        _focusVisible={{ outline: 'none', boxShadow: 'focusAi' }}
        asChild
      >
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={detailsId}
          onClick={() => setIsOpen((open) => !open)}
        >
          {label}
          <Icon
            as={isOpen ? MdExpandMore : MdChevronRight}
            boxSize="14px"
            flexShrink={0}
            aria-hidden
          />
        </button>
      </MFlex>
      {isOpen && (
        <MFlex
          id={detailsId}
          direction="column"
          gap={2}
          pl={7}
          pr={1.5}
          py={1.5}
        >
          {reason && (
            <MText fontSize="xs" color="fg.muted">
              {reason}
            </MText>
          )}
          {hasPayload(step.input) && (
            <Payload label="Input" payload={step.input} />
          )}
          {step.output && <Payload label="Output" payload={step.output} />}
        </MFlex>
      )}
    </MBox>
  );
};

/** The live line under the steps when no tool is running ("Thinking"). */
const PhaseRow: FC<{ label: string; isReconnecting: boolean }> = ({
  label,
  isReconnecting,
}) => (
  <MFlex alignItems="center" gap={2} py={1} px={1.5}>
    <MSpinner
      size="xs"
      color={isReconnecting ? 'tOrange.base' : 'action.ai'}
      borderWidth="1.5px"
      boxSize="12px"
      flexShrink={0}
      aria-hidden
    />
    <MText fontSize="xs" {...SHIMMER_PROPS}>
      {label}
    </MText>
  </MFlex>
);

export interface ActivityGroupProps {
  /** Omitted for a streaming turn that has no activity record. */
  activity?: TurnActivity;
  segmentIndex?: number;
  showAvatar: boolean;
  /** Work in progress right now, rather than a record of past work. */
  isLive: boolean;
  isReconnecting?: boolean;
}

const getDoneLabel = (
  activity: TurnActivity,
  segmentIndex: number,
  startedAt: number,
  endedAt: number,
) => {
  const elapsed = endedAt - startedAt;
  const isStopped =
    activity.status === 'stopped' &&
    segmentIndex === activity.segments.length - 1;
  if (isStopped) {
    return elapsed < 1000
      ? 'Stopped'
      : `Stopped after ${formatElapsed(elapsed)}`;
  }
  return elapsed < 1000 ? 'Worked' : `Worked for ${formatElapsed(elapsed)}`;
};

/**
 * One stretch of the agent's work in a turn. While live, a single line that
 * keeps up with what it's doing ("Working for 12s · Searching offerings");
 * after, "Worked for 27s". Either way the timeline of steps (and each tool
 * call's reasoning, input and output) is a click away.
 */
const ActivityGroupComponent: FC<ActivityGroupProps> = ({
  activity,
  segmentIndex = 0,
  showAvatar,
  isLive,
  isReconnecting = false,
}) => {
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const stepsId = useId();
  const segment = activity?.segments[segmentIndex];
  const steps = segment?.steps ?? [];
  const isExpanded = userExpanded ?? false;
  const liveLabel = activity
    ? getLiveActivityLabel(activity, isReconnecting)
    : isReconnecting
      ? RECONNECTING_LABEL
      : 'Thinking';
  const steadyLiveLabel = useSteadyValue(liveLabel, LIVE_LABEL_HOLD_MS);
  const hasRunningStep = !!segment && !!getRunningStep(segment);
  const canToggle = steps.length > 0 || isLive;

  const headerText =
    isLive || !activity || !segment ? (
      activity ? (
        <ElapsedSince startedAt={segment?.startedAt ?? activity.startedAt} />
      ) : (
        'Working…'
      )
    ) : (
      getDoneLabel(
        activity,
        segmentIndex,
        segment.startedAt,
        segment.endedAt ?? activity.endedAt ?? segment.startedAt,
      )
    );

  const header = (
    <MFlex alignItems="center" gap={2} minW={0}>
      {showAvatar && <MontyAvatar boxSize={7} isWorking={isLive} />}
      <MText fontSize="xs" fontWeight={500} color="fg.muted" flexShrink={0}>
        {headerText}
      </MText>
      {isLive && !isExpanded && (
        <MFlex alignItems="center" gap={1} minW={0}>
          <MText fontSize="xs" color="fg.subtle" aria-hidden>
            ·
          </MText>
          <MBox
            key={steadyLiveLabel}
            minW={0}
            animation={`${labelEnter} 200ms ease-out`}
            _motionReduce={{ animation: 'none' }}
          >
            <MText fontSize="xs" truncate {...SHIMMER_PROPS}>
              {steadyLiveLabel}
            </MText>
          </MBox>
        </MFlex>
      )}
      {canToggle && (
        <Icon
          as={isExpanded ? MdExpandMore : MdChevronRight}
          boxSize="16px"
          color="fg.subtle"
          flexShrink={0}
          aria-hidden
        />
      )}
    </MFlex>
  );

  return (
    <MFlex direction="column" gap={1} width="100%" minW={0}>
      {canToggle ? (
        <MFlex
          alignSelf="flex-start"
          maxW="100%"
          borderRadius="lg"
          pr={1.5}
          cursor="pointer"
          _hover={{ '& p': { color: 'fg.strong' } }}
          _focusVisible={{ outline: 'none', boxShadow: 'focusAi' }}
          asChild
        >
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={stepsId}
            onClick={() => setUserExpanded(!isExpanded)}
          >
            {header}
          </button>
        </MFlex>
      ) : (
        header
      )}
      {isExpanded && canToggle && (
        <MFlex
          id={stepsId}
          direction="column"
          ml={showAvatar ? 3 : 1}
          pl={2}
          borderLeft="2px solid"
          borderColor="border.subtle"
        >
          {buildTimeline(steps).map(({ step, reason }) => (
            <StepRow key={step.id} step={step} reason={reason} />
          ))}
          {isLive && !hasRunningStep && (
            <PhaseRow label={liveLabel} isReconnecting={isReconnecting} />
          )}
        </MFlex>
      )}
    </MFlex>
  );
};

export const ActivityGroup = memo(ActivityGroupComponent);
